<template>
  <div class="w-full h-full px-3 py-5">
    <loader v-if="isLoading" size="xxs" :loader-image="false" />
    <template v-else>
      <div class="flex flex-col gap-6">
        <div class="flex justify-start items-center gap-8">
          <div class="flex justify-start items-center gap-0">
            <back-button
              @onClick="$router.back()"
              variant="secondary"
              style="padding: 0"
            />
            <h1 class="text-xl text-left font-extrabold">Leave Summary</h1>
          </div>
          <breadcrumb :items="breadcrumbs" />
        </div>

        <div class="grid grid-cols-4 gap-4">
          <DashboardCard
            title="EMPLOYEE NAME"
            :value="`${employeeInfo.fname} ${employeeInfo.lname}`"
            icon="user-bold-rounded"
            background-color="#FCF4FF"
          />
          <DashboardCard
            title="JOB TITLE"
            :value="employeeInfo.orgFunction ? employeeInfo.orgFunction.name : '---'"
            icon="bag-solid"
          />
          <DashboardCard
            title="LEVEL"
            :value="employeeInfo.userLevel ? employeeInfo.userLevel.name : '---'"
            icon="hexagon-ring"
          />
          <DashboardCard
            title="FUNCTION"
            :value="employeeInfo.userDesignation ? employeeInfo.userDesignation.name : '---'"
            icon="baggage-solid"
          />
        </div>

        <div class="line"></div>

          <c-table
            :headers="tableHeaders"
            :items="leaveCategories"
            :has-number="false"
            aria-label="leave summary table"
            v-if="leaveCategories.length"
          >
            <template v-slot:item="{ item }">
              <div v-if="item.name" class="text-darkPurple text-sm">
                {{ item.data.name }}
              </div>
              <div v-if="item.totalEntitlementDays" class="text-darkPurple text-sm">
                <div class="flex flex-col gap-1">
                  <span>{{ (Math.round(item.data.totalEntitlementDays * 100) / 100).toFixed(2)  }}</span>
                  <span
                    v-if="item.data.carryForward && parseInt(item.data.carryForward) > 0"
                    class="text-xs text-romanSilver"
                  >
                    Carry forward {{ item.data.carryForward }} {{ parseInt(item.data.carryForward) === 1 ? 'day' : 'days' }}
                  </span>
                </div>
              </div>
              <div v-if="item.totalAccruedDays" class="text-darkPurple text-sm">
                {{ (Math.round(item.data.totalAccruedDays * 100) / 100).toFixed(2) }}
              </div>
              <div v-if="item.daysUsed" class="text-darkPurple text-sm">
                 {{(Math.round(item.data.daysUsed * 100) / 100).toFixed(2)}}
              </div>
              <div v-if="item.balance" class="text-darkPurple text-sm">
                <div class="flex flex-col gap-1">
                  <span>{{ (Math.round(item.data.balance * 100) / 100).toFixed(2) }}</span>
                  <span
                    v-if="item.data.carryForwardBalance && parseInt(item.data.carryForwardBalance) > 0"
                    class="text-xs text-romanSilver"
                  >
                    Carry forward {{ item.data.carryForwardBalance }} {{ parseInt(item.data.carryForwardBalance) === 1 ? 'day' : 'days' }}
                  </span>
                </div>
              </div>
            </template>
          </c-table>
          <div
            v-else
            class="flex flex-col justify-center items-center gap-4 py-10"
          >
            <p class="text-sm text-jet text-center">
              No leave data available for this employee.
            </p>
          </div>

        <div class="line"></div>

        <div class="flex justify-start items-center">
          <button
            @click="viewLeaveHistory"
            class="bg-dynamicBackBtn text-white rounded px-6 py-2 text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            View Leave History
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button";
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import CTable from "@scelloo/cloudenly-ui/src/components/table";
import Loader from "@scelloo/cloudenly-ui/src/components/loader";
import DashboardCard from "@/components/DashboardCard";

export default {
  name: "ViewLeaveSummary",
  components: {
    BackButton,
    Breadcrumb,
    CTable,
    Loader,
    DashboardCard,
  },
  data() {
    return {
      isLoading: false,
      employeeInfo: {
        fname: "",
        lname: "",
        orgFunction: null,
        userLevel: null,
        userDesignation: null,
      },
      leaveCategories: [],
      leaveYear: new Date().getFullYear(),
    };
  },
  computed: {
    breadcrumbs() {
      return [
        { disabled: false, text: "Leave", id: "Leave" },
        {
          disabled: false,
          text: "Year Leave Balances",
          id: "Year Leave Balances",
        },
        {
          disabled: false,
          text: `${this.$route.params.leaveYear} Year Leave Balances`,
        },
        {
          text: "Leave Summary",
          id: "Leave Summary",
        },
      ];
    },
    tableHeaders() {
      return [
        { title: "LEAVE TYPE", value: "name" },
        { title: "TOTAL ENTITLEMENT DAYS", value: "totalEntitlementDays" },
        { title: "TOTAL ACCRUED DAYS", value: "totalAccruedDays" },
        { title: "TOTAL DAYS TAKEN (TO DATE)", value: "daysUsed" },
        { title: "LEAVE BALANCE", value: "balance" },
      ];
    },
  },
  methods: {
    async fetchLeaveSummary() {
      try {
        this.isLoading = true;
        const userId = this.$route.params.id;
        this.leaveYear = this.$route.params.leaveYear || new Date().getFullYear();

        if (!userId) {
          this.$toasted.error("Employee ID is required", { duration: 3000 });
          this.isLoading = false;
          return;
        }

        const response = await this.$_getLeaveSummary({ userId, year: this.leaveYear });
        const data = response.data;

        if (data.userData) {
          const empInfo = data.userData;
          this.employeeInfo = {
            fname: empInfo.fname || "",
            lname: empInfo.lname || "",
            orgFunction: empInfo.orgFunction || empInfo.function || null,
            userLevel: empInfo.userLevel || empInfo.level || null,
            userDesignation: empInfo.userDesignation || empInfo.designation || null,
          };
        }

        if (data.leaveCategories && Array.isArray(data.leaveCategories)) {
          this.leaveCategories = data.leaveCategories.map(category => ({
            name: category.name || "",
            totalEntitlementDays: category.totalEntitledDays || "0",
            carryForward: category.carryForward || "0",
            totalAccruedDays: category.totalAccruedDays || category.totalAccrued || "0",
            daysUsed: category.totalDaysUsed || "0",
            balance: category.balance || category.leaveBalance || "0",
            carryForwardBalance: category.carryForwardBalance || "0",
          }));
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to load leave summary";
        this.$toasted.error(errorMessage, { duration: 3000 });
        this.leaveCategories = [];
      } finally {
        this.isLoading = false;
      }
    },
    viewLeaveHistory() {
      this.$router.push({
        name: "LeaveHistory",
        params: { id: this.$route.params.id },
      });
    },
  },
  mounted() {
    this.fetchLeaveSummary();
  },
};
</script>

<style scoped>
.line {
  width: 100%;
  height: 1px;
  margin: 8px auto;
  padding: 0;
  background: #878E99;
}
</style>

